import React, { useState, useEffect } from "react";
import { ProjectVideo } from "@/services/api";
import transformYouTubeUrl from "@/utils/transformYouTubeUrl";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import useEmblaCarousel from "embla-carousel-react";
import { getLightColor } from "@/utils/colorUtils";

interface ProjectVideoSectionProps {
  videos: Record<string, ProjectVideo[]>;
  customerColor?: string;
}

const ProjectVideoSection: React.FC<ProjectVideoSectionProps> = ({
  videos,
  customerColor,
}) => {
  const allVideos = Object.values(videos).flat();
  const { t } = useLanguage();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [expandedDescriptions, setExpandedDescriptions] = useState<
    Record<number, boolean>
  >({});
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on("select", () => {
      setCurrentSlide(emblaApi.selectedScrollSnap());
    });
  }, [emblaApi]);

  if (!videos || allVideos.length === 0) return null;

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  const toggleDescription = (index: number) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getIframeSrc = (url: string) => transformYouTubeUrl(url);

  const bgStyle = {
    backgroundColor: getLightColor(customerColor || "#00383c"),
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto">
      <h2 className="text-2xl font-semibold mb-2 text-left">
        {t("dashboard.videos.title")}
      </h2>
      <p className="text-gray-600 mb-6 text-left">
        {t("dashboard.videos.subtitle")}
      </p>
      <div
        className="w-full max-w-none px-0 flex flex-col items-center mb-8 rounded-xl"
        style={bgStyle}
      >
        <div className="w-full max-w-[1120px] px-2 py-8">
          <div className="embla overflow-hidden" ref={emblaRef}>
            <div className="embla__container flex">
              {allVideos.map((video, index) => (
                <div
                  key={index}
                  className="embla__slide flex flex-col lg:flex-row items-center justify-center w-full gap-0 md:gap-6 lg:gap-12 min-h-[380px]"
                >
                  {/* Left: Video */}
                  <div className="w-full lg:w-[54%] flex flex-col items-center lg:items-start">
                    <div className="rounded-xl overflow-hidden shadow-lg w-full max-w-[480px] min-w-[260px] aspect-video flex-shrink-0 bg-black">
                      <iframe
                        src={getIframeSrc(video.url)}
                        title={video.title}
                        className="w-full h-full min-h-[220px] rounded-xl"
                        allowFullScreen
                        allow="autoplay"
                      />
                    </div>
                  </div>
                  {/* Right: Info */}
                  <div className="w-full lg:w-[46%] px-0 lg:pl-4 mt-6 lg:mt-0 flex flex-col justify-center items-center lg:items-start text-left h-full">
                    <h3 className="text-2xl font-semibold mb-3 leading-snug text-gray-900">
                      {video.title}
                    </h3>
                    {video.description && (
                      <div className="relative">
                        <p
                          className={`text-base md:text-lg text-muted-foreground whitespace-pre-line ${
                            !expandedDescriptions[index] ? "line-clamp-5" : ""
                          }`}
                        >
                          {video.description}
                        </p>
                        {video.description.length > 200 && (
                          <button
                            onClick={() => toggleDescription(index)}
                            className="mt-1.5 flex items-center text-sm font-medium hover:opacity-80 transition-opacity"
                            style={{ color: customerColor }}
                          >
                            {expandedDescriptions[index] ? (
                              <>
                                {t("common.showLess")}
                                <ChevronUp className="ml-1 h-4 w-4" />
                              </>
                            ) : (
                              <>
                                {t("common.showMore")}
                                <ChevronDown className="ml-1 h-4 w-4" />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Carousel Controls */}
        <div className="flex items-center gap-4 justify-center mt-2 pb-6">
          <button
            onClick={scrollPrev}
            className="rounded-lg p-2 transition-all hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              backgroundColor: "transparent",
              border: `1px solid ${customerColor || "gray"}`,
            }}
            aria-label={t("common.previousVideo")}
          >
            <ChevronLeft className="h-5 w-5" style={{ color: customerColor }} />
          </button>
          {/* Displayed video indicator */}
          <div className="flex gap-2">
            {allVideos.map((_, idx) => (
              <button
                key={idx}
                onClick={() => emblaApi?.scrollTo(idx)}
                aria-label={t("common.selectVideo") + ` ${idx + 1}`}
                className={`w-2 h-2 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2`}
                style={{
                  backgroundColor:
                    idx === currentSlide ? customerColor : "lightgray",
                }}
              />
            ))}
          </div>
          <button
            onClick={scrollNext}
            className="rounded-lg p-2 transition-all hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              backgroundColor: "transparent",
              border: `1px solid ${customerColor || "gray"}`,
            }}
            aria-label={t("common.nextVideo")}
          >
            <ChevronRight
              className="h-5 w-5"
              style={{ color: customerColor }}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectVideoSection;
